import { supabase } from '@/lib/supabase';
import * as Sentry from '@sentry/react';

import { getStaffLimit } from '@/lib/planFeatures';

// --- A. Logica dei Limiti e Validazione (inviteUser) ---
// Contano gli utenti attivi più gli inviti già in sospeso: il limite vale solo
// per i nuovi inviti, chi è già dentro non viene mai disattivato.
export const validateUserInviteLimit = (users: any[], planId?: string, pendingInvites: number = 0) => {
    const limit = getStaffLimit(planId);
    if (limit === null) return { isValid: true, error: null };

    const occupied = users.filter((u) => u.is_active !== false).length + pendingInvites;
    if (occupied >= limit) {
        return {
            isValid: false,
            error: `Limite di ${limit} collaboratori raggiunto per il tuo piano: passa a un piano superiore per aggiungerne altri.`
        };
    }
    return { isValid: true, error: null };
};

// --- B. Gestione File e Path Generators ---
export const validateFileSize = (file: File, maxMb: number = 10) => {
    if (file.size > maxMb * 1024 * 1024) {
        return { isValid: false, error: 'File troppo pesante' };
    }
    return { isValid: true, error: null };
};

export const getShopBannerPath = (shopId: string | number, fileType: string = 'image/webp') => {
    const ext = fileType.split('/')[1];
    const dir = `${shopId}`;
    const namePrefix = `${shopId}.`;
    const fileName = `${dir}/${shopId}.${ext}`;

    return { dir, namePrefix, fileName };
};

export const getShopLogoPath = (shopId: string | number, fileType: string = 'image/webp') => {
    const ext = fileType.split('/')[1];
    const dir = `${shopId}`;
    const namePrefix = `${shopId}.`;
    const fileName = `${dir}/${shopId}.${ext}`;

    return { dir, namePrefix, fileName };
};

export const getShopGalleryImagePath = (shopId: string | number, imageId: string | number, fileType: string = 'image/webp') => {
    const ext = fileType.split('/')[1];
    return `${shopId}/${shopId}_gall_${imageId}.${ext}`;
};

// --- C. Logica di Cancellazione (deleteUser) ---
export const getDeletionStrategy = (bookingsCount: number | null) => {
    const hasBookings = bookingsCount !== null && bookingsCount > 0;
    return hasBookings ? 'SOFT_DELETE' : 'HARD_DELETE';
};

export const deleteUserService = async (id: string, bookingsCount: number | null) => {
    try {
        const strategy = getDeletionStrategy(bookingsCount);

        if (strategy === 'SOFT_DELETE') {
            // Disattiva performance future
            const { data: performances } = await supabase
                .from('Performances')
                .select('*')
                .eq('hairdresser_id', id)
                .eq('is_active', true);

            if (performances && performances.length > 0) {
                const { error: performancesError } = await supabase
                    .from('Performances')
                    .update({ is_active: false })
                    .eq('hairdresser_id', id);

                if (performancesError) throw new Error("Errore disattivazione performance");
            }

            // Disattiva utente
            const { error } = await supabase
                .from('Hairdressers')
                .update({ is_active: false })
                .eq('id', id);

            if (error) throw new Error("Errore disattivazione utente");

            return { success: true, isSoftDelete: true, error: '' };
        } else {
            // HARD DELETE (Eliminazione definitiva)
            const { error: userError } = await supabase
                .from('Hairdressers')
                .delete()
                .eq('id', id);

            if (userError) throw userError;

            return { success: true, isSoftDelete: false, error: '' };
        }
    } catch (e: any) {
        Sentry.captureException(e, {
            tags: { feature: 'users', action: 'delete_user' },
            extra: { id, bookingsCount }
        });
        console.error("Errore nell'eliminazione dell'utente:", e);
        return { success: false, error: e.message };
    }
};
